import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils';

export interface DrawerProps {
  /**
   * Whether the Drawer is open
   */
  isOpen?: boolean;
  /**
   * Callback function when close button is clicked
   */
  onClose?: () => void;
  /**
   * Callback function when back button is clicked
   */
  onBack?: () => void;
  /**
   * Whether to show the back button
   * @default false
   */
  showBackButton?: boolean;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Border radius style for the content area
   * @default "rounded-t-3xl" (top corners only)
   */
  borderRadius?: string;
  /**
   * Top offset for the content area
   * @default "top-[51px]"
   */
  topOffset?: string;
  /**
   * Close button position
   */
  closeButtonPosition?: {
    top: string;
    right: string;
  };
  /**
   * Back button position
   */
  backButtonPosition?: {
    top: string;
    left: string;
  };
  /**
   * Additional CSS classes for the Drawer container
   */
  overlayClassName?: string;
  /**
   * Additional CSS classes for the Drawer content
   */
  contentClassName?: string;
  /**
   * Children content to render inside the Drawer
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the child content
   */
  childClass?: string;
  /**
   * Additional CSS classes for the inner content
   */
  innerContentClassName?: string;
}

export interface DrawerTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  drawer: React.ReactNode;
}

const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      isOpen = false,
      onClose,
      onBack,
      showBackButton = false,
      showCloseButton = true,
      borderRadius = 'rounded-t-3xl',
      topOffset = 'top-[51px]',
      closeButtonPosition = {
        top: 'top-[15px]',
        right: 'right-14',
      },
      backButtonPosition = {
        top: 'top-[75px]',
        left: 'left-6',
      },
      overlayClassName,
      contentClassName,
      childClass,
      children,
      innerContentClassName = 'px-4 lg:px-16 pt-8',
      ...props
    },
    ref
  ) => {
    // Animation state management
    const [isVisible, setIsVisible] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    // Handle opening animation
    React.useEffect(() => {
      const handleOpen = () => {
        setIsVisible(true);
        // Small delay to ensure the element is rendered before animation starts
        const timer = setTimeout(() => {
          setIsAnimating(true);
        }, 10);
        return timer;
      };

      const handleClose = () => {
        setIsAnimating(false);
        // Keep the element visible during closing animation
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 300); // Match the animation duration
        return timer;
      };

      const timer = isOpen ? handleOpen() : handleClose();

      return () => clearTimeout(timer);
    }, [isOpen]);

    // Handle escape key press
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose?.();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        // Prevent background scroll
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose]);

    // Don't render if not visible
    if (!isVisible) {
      return null;
    }

    const drawerContent = (
      <div
        ref={ref}
        className={cn(
          'fixed inset-0 z-(--z-drawer) flex flex-col items-center justify-center w-full h-full min-h-screen transition-all duration-300 ease-out bg-magical-moonlight-d4/50',
          isAnimating ? 'opacity-100' : 'opacity-0',
          overlayClassName
        )}
        {...props}
      >
        {/* Content area with gradient background and slide-up animation */}
        <div
          className={cn(
            'absolute left-0 right-0 bottom-0 transition-transform duration-300 ease-out bg-magical-moonlight-s-cardbg',
            borderRadius,
            topOffset,
            isAnimating ? 'translate-y-0' : 'translate-y-full',
            contentClassName
          )}
        />

        {/* Close button */}
        {showCloseButton ? (
          <button
            onClick={onClose}
            className={cn(
              'absolute text-white hover:text-gray-300 transition-colors',
              closeButtonPosition.top,
              closeButtonPosition.right
            )}
            title="Close Drawer"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="w-6 h-6 md:w-8 md:h-8"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        {/* Back button */}
        {showBackButton ? (
          <button
            onClick={onBack}
            className={cn(
              'absolute bg-white rounded-full p-1 flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm w-8 h-8',
              backButtonPosition.top,
              backButtonPosition.left
            )}
            title="Go back"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="w-4 h-4 md:w-6 md:h-6"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        {/* Content with slide-up animation */}
        <div
          className={cn(
            'absolute left-0 right-0 bottom-0 mx-auto z-(--z-drawer)',
            'w-full flex flex-col items-center justify-center',
            'transition-transform duration-300 ease-out overflow-y-auto',
            borderRadius,
            topOffset,
            innerContentClassName,

            isAnimating ? 'translate-y-0' : 'translate-y-full'
          )}
          id="drawer-content"
        >
          <div className={cn('w-full h-full max-w-(--max-width)', childClass)}>
            {children}
          </div>
        </div>
      </div>
    );

    return createPortal(drawerContent, document.body);
  }
);

const DrawerTrigger = React.forwardRef<HTMLDivElement, DrawerTriggerProps>(
  ({ className, children, drawer, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    // Clone the drawer element and pass the isOpen state and onClose handler
    const drawerElement = React.isValidElement(drawer)
      ? React.cloneElement(drawer as React.ReactElement<DrawerProps>, {
          isOpen,
          onClose: () => setIsOpen(false),
          onBack: () => setIsOpen(false),
        })
      : drawer;

    return (
      <>
        <div ref={ref} className={cn('inline-block', className)} {...props}>
          <div
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
          >
            {children}
          </div>
        </div>
        {drawerElement}
      </>
    );
  }
);

Drawer.displayName = 'Drawer';
DrawerTrigger.displayName = 'DrawerTrigger';

export { Drawer, DrawerTrigger };
export default Drawer;
